import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Save, Plus, Trash2, Building2, CreditCard, Wallet, Globe, DollarSign, Settings } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";
import { EmailSettingsSection } from "@/components/EmailSettingsSection";

const uid = () => `bank_${Math.random().toString(36).slice(2, 10)}`;
const INPUT = "w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-white";

export default function AdminSettingsPage() {
    const { user, loading: authLoading } = useAuth() || {};
    const navigate = useNavigate();
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");
    const [msgErr, setMsgErr] = useState(false);

    useEffect(() => {
        if (authLoading) return;
        if (!user || !["super_admin", "admin"].includes(user.role)) return navigate("/staff-login");
        api.get("/admin/settings").then((r) => setForm(r.data));
    }, [user, authLoading]); // eslint-disable-line

    const save = async () => {
        setSaving(true);
        setMsg("");
        try {
            const r = await api.put("/admin/settings", form);
            setForm(r.data);
            setMsgErr(false);
            setMsg("Ayarlar kaydedildi.");
            setTimeout(() => setMsg(""), 3000);
        } catch (e) {
            setMsgErr(true);
            setMsg(e.response?.data?.detail || "Kaydetme başarısız");
        } finally {
            setSaving(false);
        }
    };

    if (!form) return <AdminLayout title="Ayarlar"><div>Yükleniyor...</div></AdminLayout>;

    const update = (k, v) => setForm({ ...form, [k]: v });
    const updateBank = (idx, patch) => {
        const next = [...(form.bank_accounts || [])];
        next[idx] = { ...next[idx], ...patch };
        update("bank_accounts", next);
    };
    const addBank = () => {
        const next = [...(form.bank_accounts || []), { id: uid(), bank_name: "", account_holder: "", iban: "", branch: "", swift: "", note: "" }];
        update("bank_accounts", next);
    };
    const removeBank = (idx) => {
        const next = [...(form.bank_accounts || [])];
        next.splice(idx, 1);
        update("bank_accounts", next);
    };
    const updateCurrency = (idx, patch) => {
        const next = [...(form.supported_currencies || [])];
        next[idx] = { ...next[idx], ...patch };
        update("supported_currencies", next);
    };
    const updateLanguage = (idx, patch) => {
        const next = [...(form.supported_languages || [])];
        next[idx] = { ...next[idx], ...patch };
        update("supported_languages", next);
    };
    const togglePM = (k) => update("payment_methods_enabled", { ...(form.payment_methods_enabled || {}), [k]: !(form.payment_methods_enabled || {})[k] });

    return (
        <AdminLayout
            title="Ayarlar"
            action={
                <button data-testid="save-settings" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold disabled:opacity-60">
                    <Save className="h-4 w-4" /> {saving ? "Kaydediliyor..." : "Kaydet"}
                </button>
            }
        >
            <div className="max-w-4xl space-y-6">
                {msg && <div data-testid={msgErr ? "settings-error" : "settings-msg"} className={`p-3 rounded-xl border text-sm ${msgErr ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-emerald-50 border-emerald-200 text-emerald-700"}`}>{msg}</div>}

                <Section icon={<Settings className="h-4 w-4" />} title="Site Bilgileri">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field label="Site Adı"><input data-testid="setting-site-name" value={form.site_name || ""} onChange={(e) => update("site_name", e.target.value)} className={INPUT} /></Field>
                        <Field label="Varsayılan Para Birimi (DB)"><select value={form.currency || "TRY"} onChange={(e) => update("currency", e.target.value)} className={INPUT}><option value="TRY">TRY (₺)</option><option value="EUR">EUR (€)</option><option value="USD">USD ($)</option></select></Field>
                        <Field label="İletişim E-posta"><input data-testid="setting-email" type="email" value={form.contact_email || ""} onChange={(e) => update("contact_email", e.target.value)} className={INPUT} /></Field>
                        <Field label="İletişim Telefon"><input data-testid="setting-phone" value={form.contact_phone || ""} onChange={(e) => update("contact_phone", e.target.value)} className={INPUT} /></Field>
                    </div>
                </Section>

                <EmailSettingsSection value={form.email_settings} onChange={(v) => update("email_settings", v)} Section={Section} Field={Field} />

                <Section icon={<CreditCard className="h-4 w-4" />} title="Ödeme Yöntemleri">
                    <p className="text-xs text-slate-500 mb-3">Misafire checkout sayfasında hangi yöntemler görünsün?</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <PaymentToggle testId="pm-toggle-bank" icon={<Building2 className="h-4 w-4 text-amber-500" />} label="Havale / EFT" enabled={!!(form.payment_methods_enabled || {}).bank_transfer} onChange={() => togglePM("bank_transfer")} />
                        <PaymentToggle testId="pm-toggle-hotel" icon={<Wallet className="h-4 w-4 text-emerald-500" />} label="Otelde Ödeme" enabled={!!(form.payment_methods_enabled || {}).at_hotel} onChange={() => togglePM("at_hotel")} />
                    </div>
                </Section>

                <Section icon={<CreditCard className="h-4 w-4" />} title="Kredi/Banka Kartı (Sanal POS Bankaları)">
                    <p className="text-xs text-slate-500 mb-3">Sanal POS anlaşmalı bankalarınızı seçin. Misafir checkout'ta bunlardan birini seçerek kartla ödeme yapacak. (Şu an manuel kayıt; gerçek POS entegrasyonu iyzico/PayTR ile gelir.)</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {[
                            { v: "isbank", l: "İş Bankası", color: "bg-blue-100 text-blue-700" },
                            { v: "garanti", l: "Garanti BBVA", color: "bg-emerald-100 text-emerald-700" },
                            { v: "yapikredi", l: "Yapı Kredi", color: "bg-blue-100 text-blue-700" },
                            { v: "akbank", l: "Akbank", color: "bg-red-100 text-red-700" },
                            { v: "vakifbank", l: "VakıfBank", color: "bg-amber-100 text-amber-700" },
                            { v: "ziraat", l: "Ziraat Bankası", color: "bg-red-100 text-red-700" },
                            { v: "halkbank", l: "Halkbank", color: "bg-blue-100 text-blue-700" },
                            { v: "qnb", l: "QNB Finansbank", color: "bg-violet-100 text-violet-700" },
                            { v: "denizbank", l: "DenizBank", color: "bg-cyan-100 text-cyan-700" },
                            { v: "teb", l: "TEB", color: "bg-emerald-100 text-emerald-700" },
                            { v: "ing", l: "ING Türkiye", color: "bg-orange-100 text-orange-700" },
                            { v: "turkiyefinans", l: "Türkiye Finans", color: "bg-emerald-100 text-emerald-700" },
                            { v: "fibabanka", l: "Fibabanka", color: "bg-fuchsia-100 text-fuchsia-700" },
                            { v: "albaraka", l: "Albaraka Türk", color: "bg-amber-100 text-amber-700" },
                            { v: "kuveytturk", l: "Kuveyt Türk", color: "bg-rose-100 text-rose-700" },
                        ].map((bank) => {
                            const isEnabled = !!(form.pos_banks || {})[bank.v];
                            return (
                                <label key={bank.v} data-testid={`pos-bank-${bank.v}`} className={`flex items-center gap-2 p-2.5 rounded-xl border-2 cursor-pointer transition-all ${isEnabled ? "border-sky-500 bg-sky-50 dark:bg-sky-950/30" : "border-slate-200 dark:border-slate-700 hover:border-slate-300"}`}>
                                    <input type="checkbox" checked={isEnabled}
                                        onChange={() => update("pos_banks", { ...(form.pos_banks || {}), [bank.v]: !isEnabled })}
                                        className="h-4 w-4 accent-sky-500" />
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${bank.color}`}>{bank.l}</span>
                                </label>
                            );
                        })}
                    </div>
                </Section>

                <Section
                    icon={<Building2 className="h-4 w-4" />}
                    title="Banka Hesapları"
                    action={
                        <button data-testid="add-bank-btn" onClick={addBank} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-xs font-semibold">
                            <Plus className="h-3.5 w-3.5" /> Banka Ekle
                        </button>
                    }
                >
                    <p className="text-xs text-slate-500 mb-3">Havale/EFT ile ödeyenlere checkout'ta gösterilen hesaplar. Birden fazla banka ekleyebilirsiniz.</p>
                    {(form.bank_accounts || []).length === 0 ? (
                        <div className="p-6 text-center text-sm text-slate-500 italic border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl">
                            Henüz banka hesabı eklenmedi.
                        </div>
                    ) : (
                        <div className="space-y-4" data-testid="bank-list">
                            {(form.bank_accounts || []).map((b, i) => (
                                <div key={b.id || i} data-testid={`bank-row-${i}`} className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                                    <div className="flex items-start justify-between gap-2 mb-3">
                                        <div className="font-display font-bold text-slate-900 dark:text-white">#{i + 1}</div>
                                        <button onClick={() => removeBank(i)} data-testid={`remove-bank-${i}`} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600">
                                            <Trash2 className="h-4 w-4" />
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <Field label="Banka Adı *"><input data-testid={`bank-name-${i}`} value={b.bank_name || ""} onChange={(e) => updateBank(i, { bank_name: e.target.value })} placeholder="Garanti BBVA" className={INPUT} /></Field>
                                        <Field label="Hesap Sahibi *"><input data-testid={`bank-holder-${i}`} value={b.account_holder || ""} onChange={(e) => updateBank(i, { account_holder: e.target.value })} placeholder="msOnline Turizm A.Ş." className={INPUT} /></Field>
                                        <Field label="IBAN *"><input data-testid={`bank-iban-${i}`} value={b.iban || ""} onChange={(e) => updateBank(i, { iban: e.target.value.toUpperCase() })} placeholder="TR00 0000 0000 0000 0000 0000 00" className={`${INPUT} font-mono`} /></Field>
                                        <Field label="Şube"><input data-testid={`bank-branch-${i}`} value={b.branch || ""} onChange={(e) => updateBank(i, { branch: e.target.value })} placeholder="Levent" className={INPUT} /></Field>
                                        <Field label="SWIFT (yabancı havale)"><input data-testid={`bank-swift-${i}`} value={b.swift || ""} onChange={(e) => updateBank(i, { swift: e.target.value.toUpperCase() })} placeholder="TGBATRIS" className={`${INPUT} font-mono`} /></Field>
                                        <Field label="Not (opsiyonel)"><input data-testid={`bank-note-${i}`} value={b.note || ""} onChange={(e) => updateBank(i, { note: e.target.value })} placeholder="Açıklamaya PNR yazınız" className={INPUT} /></Field>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </Section>

                <Section icon={<DollarSign className="h-4 w-4" />} title="Para Birimi">
                    <p className="text-xs text-slate-500">Şu an site sadece <span className="font-bold">TRY (₺)</span> ile çalışıyor. Çoklu para birimi desteği Faz 2'de eklenecek (anlık kur entegrasyonu ile).</p>
                </Section>

                <Section icon={<Globe className="h-4 w-4" />} title="Dil Seçenekleri">
                    <p className="text-xs text-slate-500 mb-3">Public site header'ında hangi diller seçilebilir? (TR ve EN her zaman önerilir.)</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {(form.supported_languages || []).map((l, i) => (
                            <label key={l.code} data-testid={`lang-row-${l.code}`} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer ${l.enabled ? "border-sky-500 bg-sky-50 dark:bg-sky-950/30" : "border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40"}`}>
                                <input data-testid={`lang-toggle-${l.code}`} type="checkbox" checked={!!l.enabled} onChange={(e) => updateLanguage(i, { enabled: e.target.checked })} className="h-4 w-4 accent-sky-500" />
                                <span className="text-xl">{l.flag}</span>
                                <span className="flex-1 font-medium text-slate-900 dark:text-white">{l.name}</span>
                                <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{l.code}</span>
                            </label>
                        ))}
                    </div>
                    <p className="text-[11px] italic text-amber-700 dark:text-amber-300 mt-3 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 rounded-lg">
                        Şu an TR ve EN çevirileri hazır. DE / RU / AR çevirileri Faz 2'de eklenecek — şimdi aktif ederseniz İngilizce fallback gösterilir.
                    </p>
                </Section>

                <Section icon={<Settings className="h-4 w-4" />} title="Komisyon">
                    <Field label="Varsayılan Komisyon Oranı (%)">
                        <input data-testid="setting-commission" type="number" min="0" max="100" step="0.5" value={form.default_commission_percent || 0} onChange={(e) => update("default_commission_percent", Number(e.target.value))} className={INPUT} />
                    </Field>
                </Section>
            </div>
        </AdminLayout>
    );
}

function Section({ icon, title, children, action }) {
    return (
        <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    {icon}{title}
                </h2>
                {action}
            </div>
            {children}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</span>
            {children}
        </label>
    );
}

function PaymentToggle({ testId, icon, label, enabled, onChange }) {
    return (
        <label data-testid={testId} className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-colors ${enabled ? "border-sky-500 bg-sky-50 dark:bg-sky-950/30" : "border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 hover:border-slate-300"}`}>
            <input type="checkbox" checked={enabled} onChange={onChange} className="h-4 w-4 accent-sky-500" />
            {icon}
            <span className="flex-1 text-sm font-medium text-slate-900 dark:text-white">{label}</span>
        </label>
    );
}
